import { CloudUpload, Description, InsertDriveFile, Delete } from '@mui/icons-material';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    LinearProgress,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { renderAsync } from 'docx-preview';
import { heicTo } from 'heic-to';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import TicketNavigator from './TicketNavigator';
import Loader from 'components/v2/common/loader';
import PDFViewer from 'components/v2/common/pdfViewer';

import api from 'api';
import { setAlert } from 'redux/alert';
import type { ApplicationDocumentInterface, TicketInterface } from 'types';
import { getFileBlob } from 'utils/v2/file';
import { getErrorMessage } from 'utils/v2/errorHelper';

type PreviewKind = 'pdf' | 'image' | 'docx';

interface PreviewState {
    blob: Blob;
    kind: PreviewKind;
}

/**
 * Upload types aligned with:
 * - DocumentPreview reupload (CLIENTFLOW): PDF, DOCX, HEIC/HEIF
 * - Preview support in documentPreview: PDF, images, HEIC/HEIF, DOCX
 */
const ACCEPT_UPLOAD =
    'application/pdf,.pdf,' +
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,' +
    'image/heic,image/heif,.heic,.heif,' +
    'image/png,image/jpeg,image/jpg,image/gif,image/webp,image/bmp,.png,.jpg,.jpeg,.gif,.webp,.bmp';

interface DocumentUploadDialogProps {
    open: boolean;
    onClose: () => void;
    onUpload: (file: File) => Promise<void> | void;
    document?: ApplicationDocumentInterface | null;
    isUploading?: boolean;
    tickets?: TicketInterface[];
    /** @deprecated no longer used for preview; kept for call-site compatibility */
    handleAction?: (action: string, document: ApplicationDocumentInterface) => void;
}

/** Same detection as components/v2/common/documentPreview.tsx */
const isHeicFile = (contentType: string, fileUrl: string, fileName: string): boolean => {
    const lowerContentType = contentType.toLowerCase();
    if (lowerContentType.includes('image/heic') || lowerContentType.includes('image/heif')) {
        return true;
    }

    const urlPath = decodeURIComponent(fileUrl.split(/[?#]/)[0]).toLowerCase();
    if (urlPath.endsWith('.heic') || urlPath.endsWith('.heif')) {
        return true;
    }

    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith('.heic') || lowerName.endsWith('.heif')) {
        return true;
    }

    return false;
};

const isDocxFile = (contentType: string, fileUrl: string, fileName: string): boolean => {
    const lowerContentType = contentType.toLowerCase();
    const lowerUrl = fileUrl.toLowerCase();
    const lowerName = fileName.toLowerCase();

    return (
        lowerContentType.includes(
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ) ||
        lowerContentType.includes('application/msword') ||
        /\.docx?([?#].*)?$/.test(lowerUrl) ||
        /\.docx?$/.test(lowerName)
    );
};

const isImagePreview = (contentType: string, fileUrl: string, fileName: string): boolean => {
    const lowerContentType = contentType.toLowerCase();
    const lowerUrl = fileUrl.toLowerCase();
    const lowerName = fileName.toLowerCase();

    if (isHeicFile(contentType, fileUrl, fileName)) {
        return true;
    }

    return (
        lowerContentType.startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)([?#].*)?$/.test(lowerUrl) ||
        /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/.test(lowerName)
    );
};

/** Prepare blob for preview: HEIC→JPEG, detect image / docx / pdf (same as DocumentPreview). */
const preparePreviewBlob = async (
    sourceBlob: Blob,
    fileUrl: string,
    fileName: string
): Promise<PreviewState> => {
    const contentType = sourceBlob.type || '';

    if (isHeicFile(contentType, fileUrl, fileName)) {
        const jpegBlob = await heicTo({
            blob: sourceBlob,
            type: 'image/jpeg',
            quality: 0.85,
        });
        return { blob: jpegBlob, kind: 'image' };
    }

    if (isDocxFile(contentType, fileUrl, fileName)) {
        return { blob: sourceBlob, kind: 'docx' };
    }

    if (isImagePreview(contentType, fileUrl, fileName)) {
        return { blob: sourceBlob, kind: 'image' };
    }

    return { blob: sourceBlob, kind: 'pdf' };
};

const isAllowedUploadFile = (file: File): boolean => {
    const type = (file.type || '').toLowerCase();
    const name = file.name.toLowerCase();

    // PDF
    if (type === 'application/pdf' || name.endsWith('.pdf')) {
        return true;
    }
    // DOCX (DocumentPreview reupload)
    if (
        type.includes('wordprocessingml') ||
        type === 'application/msword' ||
        name.endsWith('.docx') ||
        name.endsWith('.doc')
    ) {
        return true;
    }
    // HEIC / HEIF
    if (
        type === 'image/heic' ||
        type === 'image/heif' ||
        name.endsWith('.heic') ||
        name.endsWith('.heif')
    ) {
        return true;
    }
    // Common images (preview-supported)
    if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/.test(name)) {
        return true;
    }
    return false;
};

const imagePreviewSx = {
    display: 'block',
    margin: 'auto',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
} as const;

const DocxPreview = ({ blob }: { blob: Blob }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        let cancelled = false;
        el.innerHTML = '';

        void (async () => {
            try {
                const buffer = await blob.arrayBuffer();
                if (!cancelled && containerRef.current) {
                    await renderAsync(buffer, containerRef.current);
                }
            } catch (error) {
                console.error('Failed to render DOCX preview:', error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [blob]);

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
                p: 1.5,
                bgcolor: 'background.paper',
                '& .docx-wrapper': { background: 'transparent', padding: 0 },
                '& .docx': { margin: '0 auto', boxShadow: 1 },
            }}
        >
            <Box ref={containerRef} />
        </Box>
    );
};

const DocumentUploadDialog = ({
    open,
    onClose,
    onUpload,
    document,
    isUploading = false,
    tickets = [],
}: DocumentUploadDialogProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isLoadingCurrent, setIsLoadingCurrent] = useState(false);
    const [isLoadingSample, setIsLoadingSample] = useState(false);
    const [isLoadingSelectedPreview, setIsLoadingSelectedPreview] = useState(false);
    const [documentView, setDocumentView] = useState<'sample' | 'current'>('current');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [previousPreview, setPreviousPreview] = useState<PreviewState | null>(null);
    const [samplePreview, setSamplePreview] = useState<PreviewState | null>(null);
    const [selectedPreview, setSelectedPreview] = useState<PreviewState | null>(null);

    const hasSample = !!document?.sample_uri;
    const isReplacing = !!document?.resource_name;
    const isLoadingLeftPanel = documentView === 'current' ? isLoadingCurrent : isLoadingSample;

    // Filter tickets related to this document
    const documentTickets = useMemo(() => {
        if (!document) return [];
        return tickets.filter((ticket) => ticket.document_id === document.id);
    }, [tickets, document]);

    const openTickets = documentTickets.filter(
        (t) => t.status.toLowerCase().includes('pending') || t.status.toLowerCase().includes('open')
    );

    const previousObjectUrl = useMemo(() => {
        if (!previousPreview || previousPreview.kind !== 'image') return null;
        return URL.createObjectURL(previousPreview.blob);
    }, [previousPreview]);

    const sampleObjectUrl = useMemo(() => {
        if (!samplePreview || samplePreview.kind !== 'image') return null;
        return URL.createObjectURL(samplePreview.blob);
    }, [samplePreview]);

    const selectedObjectUrl = useMemo(() => {
        if (!selectedPreview || selectedPreview.kind !== 'image') return null;
        return URL.createObjectURL(selectedPreview.blob);
    }, [selectedPreview]);

    useEffect(() => {
        return () => {
            if (previousObjectUrl) URL.revokeObjectURL(previousObjectUrl);
        };
    }, [previousObjectUrl]);

    useEffect(() => {
        return () => {
            if (sampleObjectUrl) URL.revokeObjectURL(sampleObjectUrl);
        };
    }, [sampleObjectUrl]);

    useEffect(() => {
        return () => {
            if (selectedObjectUrl) URL.revokeObjectURL(selectedObjectUrl);
        };
    }, [selectedObjectUrl]);

    // Load current uploaded document for in-dialog preview (PDF / image / HEIC)
    useEffect(() => {
        if (!open || !document?.id || !document.resource_name) {
            setPreviousPreview(null);
            setIsLoadingCurrent(false);
            return;
        }

        let cancelled = false;
        const fileName = document.resource_name || document.document_name || '';

        const loadCurrentDocumentPreview = async () => {
            setIsLoadingCurrent(true);
            setPreviousPreview(null);
            try {
                const response = await api.documentResource.get(document.id);
                const signedUrl = response.data?.url;
                if (!signedUrl) {
                    throw new Error('Preview URL unavailable');
                }
                const sourceBlob = await getFileBlob(signedUrl);
                const prepared = await preparePreviewBlob(sourceBlob, signedUrl, fileName);
                if (!cancelled) {
                    setPreviousPreview(prepared);
                }
            } catch (error) {
                console.error('Failed to load document preview:', error);
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(error, 'Failed to load document preview'),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingCurrent(false);
                }
            }
        };

        void loadCurrentDocumentPreview();

        return () => {
            cancelled = true;
        };
    }, [open, document?.id, document?.resource_name, document?.document_name, dispatch]);

    // Load sample document blob when a sample is available
    useEffect(() => {
        if (!open || !hasSample || !document?.id || !document.application_id) {
            return;
        }

        let cancelled = false;
        const fileName = document.document_name || 'sample';

        const loadSamplePreview = async () => {
            setIsLoadingSample(true);
            try {
                const response = await api.application.getDocumentSampleUrl(
                    String(document.application_id),
                    document.id
                );
                const signedUrl = response.data?.url;
                if (!signedUrl) {
                    throw new Error('Sample URL unavailable');
                }
                const sourceBlob = await getFileBlob(signedUrl);
                const prepared = await preparePreviewBlob(sourceBlob, signedUrl, fileName);
                if (!cancelled) {
                    setSamplePreview(prepared);
                }
            } catch (error) {
                console.error('Failed to load sample document:', error);
            } finally {
                if (!cancelled) {
                    setIsLoadingSample(false);
                }
            }
        };

        void loadSamplePreview();

        return () => {
            cancelled = true;
        };
    }, [open, hasSample, document?.id, document?.application_id, document?.document_name]);

    // Build right-panel preview for the newly selected file (incl. HEIC → JPEG)
    useEffect(() => {
        if (!selectedFile) {
            setSelectedPreview(null);
            setIsLoadingSelectedPreview(false);
            return;
        }

        let cancelled = false;

        const loadSelectedPreview = async () => {
            setIsLoadingSelectedPreview(true);
            setSelectedPreview(null);
            try {
                const prepared = await preparePreviewBlob(
                    selectedFile,
                    selectedFile.name,
                    selectedFile.name
                );
                if (!cancelled) {
                    setSelectedPreview(prepared);
                }
            } catch (error) {
                console.error('Failed to prepare selected file preview:', error);
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(
                                error,
                                'This HEIC image could not be converted for preview.'
                            ),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingSelectedPreview(false);
                }
            }
        };

        void loadSelectedPreview();

        return () => {
            cancelled = true;
        };
    }, [selectedFile, dispatch]);

    const handleFileChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (file) {
                if (file.size > 10 * 1024 * 1024) {
                    dispatch(setAlert({ message: `File size exceeds 10MB limit`, type: 'error' }));
                    return;
                }

                if (!isAllowedUploadFile(file)) {
                    dispatch(
                        setAlert({
                            message: `Invalid file type. Allowed: PDF, DOCX, HEIC/HEIF, and common images (PNG, JPG, etc.).`,
                            type: 'error',
                        })
                    );
                    return;
                }

                setSelectedFile(file);
            }
        },
        [dispatch]
    );

    const handleUpload = async () => {
        if (!selectedFile) {
            dispatch(setAlert({ message: `Please select a file to upload.`, type: 'error' }));
            return;
        }

        await onUpload(selectedFile);
        // Clear the file input and selected file only after upload completes
        setSelectedFile(null);
        setSelectedPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setSelectedPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClose = () => {
        if (!isUploading) {
            setSelectedFile(null);
            setSelectedPreview(null);
            setDocumentView('current');
            setSamplePreview(null);
            setPreviousPreview(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            onClose();
        }
    };

    // When there is no existing uploaded file but a sample exists, default to sample view
    useEffect(() => {
        if (!document?.resource_name && hasSample) {
            setDocumentView('sample');
        }
    }, [document?.resource_name, hasSample]);

    const renderBlobPreview = (
        preview: PreviewState | null,
        imageUrl: string | null,
        emptyLabel: string
    ) => {
        if (!preview) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <Description sx={{ fontSize: 64, mb: 1, opacity: 0.5 }} />
                    <Typography variant="body2">{emptyLabel}</Typography>
                </Box>
            );
        }

        if (preview.kind === 'image' && imageUrl) {
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1.5,
                        overflow: 'auto',
                    }}
                >
                    <Box component="img" src={imageUrl} alt={emptyLabel} sx={imagePreviewSx} />
                </Box>
            );
        }

        if (preview.kind === 'docx') {
            return <DocxPreview blob={preview.blob} />;
        }

        return (
            <Box sx={{ width: '100%', height: '100%' }}>
                <PDFViewer file={preview.blob} />
            </Box>
        );
    };

    const renderSelectedPreview = () => {
        if (!selectedFile) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: 'text.secondary',
                    }}
                >
                    <CloudUpload sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography variant="body2">No file selected</Typography>
                </Box>
            );
        }

        if (isLoadingSelectedPreview) {
            return <Loader message={t('documentsLoadingDocument')} />;
        }

        if (selectedPreview?.kind === 'image' && selectedObjectUrl) {
            return (
                <Box
                    component="img"
                    src={selectedObjectUrl}
                    alt={selectedFile.name}
                    sx={imagePreviewSx}
                />
            );
        }

        if (selectedPreview?.kind === 'docx') {
            return <DocxPreview blob={selectedPreview.blob} />;
        }

        if (selectedPreview?.kind === 'pdf') {
            return (
                <Box sx={{ width: '100%', height: '100%' }}>
                    <PDFViewer file={selectedPreview.blob} />
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary',
                }}
            >
                <InsertDriveFile sx={{ fontSize: 64, mb: 1 }} />
                <Typography variant="body2" align="center">
                    {selectedFile.name}
                </Typography>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            maxWidth="xl"
            fullWidth
            slotProps={{
                paper: { sx: { borderRadius: 1, height: '90vh' } },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h6">
                    {isReplacing ? `Replace ${document.document_name}` : 'Upload Document'}
                </Typography>
            </DialogTitle>

            {isUploading && <LinearProgress />}

            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                    px: 3,
                    py: 0.5,
                }}
            >
                {/* Preview Section */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 3,
                        flexGrow: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    {/* Left: Sample or Current Document */}
                    <Paper
                        variant="outlined"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            overflowY: 'auto',
                        }}
                    >
                        <Stack
                            direction="row"
                            sx={{ justifyContent: 'space-between', alignItems: 'center', p: 0.5 }}
                        >
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ mb: 1.5, fontWeight: 600, px: 1, pt: 1 }}
                            >
                                {documentView === 'current'
                                    ? t('documentsCurrentDocument')
                                    : t('documentsSampleDocument')}
                            </Typography>

                            {hasSample && (previousPreview || samplePreview) && (
                                <ToggleButtonGroup
                                    value={documentView}
                                    exclusive
                                    onChange={(_e, newView) => {
                                        if (newView !== null) {
                                            setDocumentView(newView);
                                        }
                                    }}
                                    size="small"
                                >
                                    <ToggleButton
                                        value="sample"
                                        aria-label="show sample"
                                        disabled={isLoadingSample && documentView === 'sample'}
                                    >
                                        <Typography variant="caption">
                                            {t('documentsSampleDocument')}
                                        </Typography>
                                    </ToggleButton>
                                    <ToggleButton
                                        value="current"
                                        aria-label="show current"
                                        disabled={!isReplacing}
                                    >
                                        <Typography variant="caption">
                                            {t('documentsCurrentDocument')}
                                        </Typography>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            )}
                        </Stack>
                        <Box
                            sx={{
                                flexGrow: 1,
                                overflow: 'auto',
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                minHeight: 0,
                                position: 'relative',
                                ...(isLoadingLeftPanel ||
                                (documentView === 'current' && !previousPreview) ||
                                (documentView === 'sample' && !samplePreview)
                                    ? {
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                      }
                                    : {}),
                            }}
                        >
                            {isLoadingLeftPanel ? (
                                <Loader message={t('documentsLoadingDocument')} />
                            ) : documentView === 'sample' ? (
                                renderBlobPreview(
                                    samplePreview,
                                    sampleObjectUrl,
                                    t('documentsSampleDocument')
                                )
                            ) : documentView === 'current' && isReplacing ? (
                                renderBlobPreview(
                                    previousPreview,
                                    previousObjectUrl,
                                    t('documentsNoPreview')
                                )
                            ) : (
                                renderBlobPreview(null, null, t('documentsNoPreview'))
                            )}
                        </Box>
                    </Paper>

                    {/* Right: Upload & Preview */}
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            gap: 2,
                            overflow: 'auto',
                        }}
                    >
                        {/* Upload Controls */}
                        <Box>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ mb: 1.5, fontWeight: 600 }}
                            >
                                Your Upload
                            </Typography>

                            {!selectedFile ? (
                                <Button
                                    variant="outlined"
                                    component="label"
                                    fullWidth
                                    startIcon={<CloudUpload />}
                                    disabled={isUploading}
                                    sx={{
                                        py: 1.5,
                                        borderStyle: 'dashed',
                                        borderWidth: 2,
                                        '&:hover': {
                                            borderStyle: 'dashed',
                                            borderWidth: 2,
                                        },
                                    }}
                                >
                                    Choose File to Upload
                                    <input
                                        hidden
                                        accept={ACCEPT_UPLOAD}
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                    />
                                </Button>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        fullWidth
                                        startIcon={<CloudUpload />}
                                        disabled={isUploading}
                                        sx={{ flex: 1 }}
                                    >
                                        Replace File
                                        <input
                                            type="file"
                                            hidden
                                            onChange={handleFileChange}
                                            accept={ACCEPT_UPLOAD}
                                        />
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleRemoveFile}
                                        disabled={isUploading}
                                        startIcon={<Delete />}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            )}

                            {selectedFile && (
                                <Box
                                    sx={{
                                        mt: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {selectedFile.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {(selectedFile.size / 1024).toFixed(2)} KB
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Preview */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                overflow: 'auto',
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                minHeight: 0,
                                position: 'relative',
                                ...(!selectedFile ||
                                selectedPreview?.kind === 'image' ||
                                isLoadingSelectedPreview
                                    ? {
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                      }
                                    : {}),
                            }}
                        >
                            {renderSelectedPreview()}
                        </Box>
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pb: 2.5,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {/* Left: Tickets */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flex: '1 1 auto',
                        mr: 2,
                        minWidth: 0,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            minWidth: 0,
                            // width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <TicketNavigator tickets={openTickets} />
                    </Box>
                </Box>

                {/* Right: Actions */}
                <Box sx={{ display: 'flex', gap: 1, flex: '0 0 auto' }}>
                    <Button onClick={handleClose} disabled={isUploading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={isUploading || !selectedFile}
                        startIcon={<CloudUpload />}
                    >
                        {isUploading ? 'Uploading...' : isReplacing ? 'Replace' : 'Upload'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default DocumentUploadDialog;
